import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reviewApi } from '../../utils/reviewApi';
import PanelFormModal from './PanelFormModal';
import ReviewPanelCard from './ReviewPanelCard';
import StartReviewModal from './StartReviewModal';
import WaitingForYou from './WaitingForYou';
import '../../styles/DocReview.css';

/**
 * Doc Review home (mockup screen 1): what is waiting for ME, then the panels.
 *
 * "Waiting for you" is the answer to "where do I see reviews I owe?" (D9) — the same list the
 * nav badge and the bell count.
 */
export default function ReviewHome({ canManagePanels }) {
  const navigate = useNavigate();
  const [panels, setPanels] = useState(null);
  const [waiting, setWaiting] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);          // {id?} — the New/Edit panel dialog
  const [starting, setStarting] = useState(null);  // {panelId, row}
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const [p, h] = await Promise.all([reviewApi.panels(), reviewApi.home()]);
      setPanels(p.panels || []);
      setWaiting(h.waiting || []);
      setError('');
    } catch (e) {
      setError(e.message);
      setPanels((x) => x || []);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (panel) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the panel “${panel.name}”? Reviews already started are kept.`)) return;
    try {
      await reviewApi.deletePanel(panel.id);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="drv-page">
      <div className="drv-head">
        <div>
          <h1 className="drv-title">Doc Review</h1>
          <p className="drv-sub">Review drafts with your team, then route them for signature in Orcanos.</p>
        </div>
        {canManagePanels && (
          <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={() => setForm({})}>
            + New panel
          </button>
        )}
      </div>

      {error && <div className="drv-error" role="alert">{error}</div>}

      <WaitingForYou waiting={waiting} />

      {panels === null && <div className="drv-muted">Loading panels…</div>}
      {panels?.length === 0 && (
        <div className="drv-empty">
          No panels yet.{' '}
          {canManagePanels
            ? <>Create one with <strong>+ New panel</strong> — pick an Orcanos DMS filter and its documents appear here.</>
            : 'Ask someone who can create panels to add one.'}
        </div>
      )}

      {(panels || []).map((p) => (
        <ReviewPanelCard
          key={p.id}
          panel={p}
          canManage={canManagePanels}
          reloadKey={reloadKey}
          onEdit={() => setForm({ id: p.id })}
          onDelete={() => remove(p)}
          onStart={(row) => setStarting({ panelId: p.id, row })}
        />
      ))}

      {form && (
        <PanelFormModal
          panelId={form.id}
          onClose={() => setForm(null)}
          onSaved={() => { setForm(null); load(); setReloadKey((k) => k + 1); }}
        />
      )}
      {starting && (
        <StartReviewModal
          setupId={starting.panelId}
          row={starting.row}
          onClose={() => setStarting(null)}
          onStarted={(id) => {
            setStarting(null);
            navigate(`/review/item/${encodeURIComponent(starting.row.item_id)}?setup=${starting.panelId}&review=${id}`);
          }}
        />
      )}
    </div>
  );
}
